import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import api, { eur, formatApiError } from "@/lib/api";
import { Card, Btn } from "@/components/common";
import { Trash2, Send, Settings2 } from "lucide-react";
import MarketingItems from "@/components/MarketingItems";
import { sendMarketingReceipt } from "@/lib/marketingReceipt";

const today = () => new Date().toISOString().slice(0, 10);
const METHODS = [["contanti", "Contanti"], ["carta", "Carta"], ["bonifico", "Bonifico"], ["altro", "Altro"]];
const inp = "px-2 py-1.5 text-sm rounded border border-slate-300 bg-white";

function SaleForm({ cfg, onSaved }) {
  const itemOf = (id) => cfg.items.find((x) => x.id === id) || cfg.items[0];
  const empty = (type) => { const it = itemOf(type); return { date: today(), type: it.id, qty: 1, unit_price: it.price, our_pct: it.our_pct ?? 0, cost: "", payment_method: "contanti", client: "", client_phone: "", notes: "" }; };
  const [f, setF] = useState(() => empty(cfg.items[0].id));
  const mode = itemOf(f.type).mode;
  const set = (k, v) => setF({ ...f, [k]: v });
  const total = Number(f.qty || 0) * Number(f.unit_price || 0);
  const ours = mode === "percent" ? total * Number(f.our_pct || 0) / 100 : total - Number(f.cost || 0);
  const save = async (send) => {
    if (mode === "cost" && f.cost === "") { toast.error("Inserisci il costo"); return; }
    try {
      const r = await api.post("/marketing/sales", { ...f, qty: Number(f.qty), unit_price: Number(f.unit_price), our_pct: Number(f.our_pct), cost: Number(f.cost || 0) });
      toast.success("Vendita registrata");
      if (send) await sendMarketingReceipt(r.data);
      setF(empty(f.type)); onSaved();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <div className="flex flex-wrap items-end gap-2" data-testid="mk-form">
      <div className="flex flex-wrap rounded-md overflow-hidden border border-slate-300">
        {cfg.items.map((it) => (
          <button key={it.id} type="button" data-testid={`mk-type-${it.id}`} onClick={() => setF(empty(it.id))} className={`px-3 py-1.5 text-sm font-semibold ${f.type === it.id ? "bg-sky-700 text-white" : "bg-white text-slate-600"}`}>{it.name}</button>
        ))}
      </div>
      <input data-testid="mk-date" type="date" value={f.date} onChange={(e) => set("date", e.target.value)} className={inp} />
      <label className="text-[11px] text-slate-500">Quantità<input data-testid="mk-qty" type="number" min="1" value={f.qty} onChange={(e) => set("qty", e.target.value)} className={`${inp} block w-16`} /></label>
      <label className="text-[11px] text-slate-500">Prezzo €<input data-testid="mk-price" type="number" value={f.unit_price} onChange={(e) => set("unit_price", e.target.value)} className={`${inp} block w-20`} /></label>
      {mode === "percent"
        ? <label className="text-[11px] text-slate-500">Mia quota %<input data-testid="mk-pct" type="number" value={f.our_pct} onChange={(e) => set("our_pct", e.target.value)} className={`${inp} block w-20`} /></label>
        : <label className="text-[11px] text-slate-500">Costo totale €<input data-testid="mk-cost" type="number" value={f.cost} onChange={(e) => set("cost", e.target.value)} className={`${inp} block w-24`} /></label>}
      <select data-testid="mk-method" value={f.payment_method} onChange={(e) => set("payment_method", e.target.value)} className={inp}>{METHODS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}</select>
      <input data-testid="mk-client" value={f.client} onChange={(e) => set("client", e.target.value)} placeholder="Cliente / camera" className={`${inp} w-40`} />
      <input data-testid="mk-phone" value={f.client_phone} onChange={(e) => set("client_phone", e.target.value)} placeholder="WhatsApp cliente" className={`${inp} w-40`} />
      <div className="text-xs text-slate-600" data-testid="mk-preview">Incasso <b>{eur(total)}</b> · a te <b className="text-emerald-700">{eur(ours)}</b>{mode === "percent" && <> · a terzi <b>{eur(total - ours)}</b></>}</div>
      <Btn data-testid="mk-save" onClick={() => save(false)}>Registra</Btn>
      <Btn data-testid="mk-save-send" variant="secondary" onClick={() => save(true)}><Send size={14} /> Registra e invia ricevuta</Btn>
    </div>
  );
}

const Stat = ({ l, v, c = "" }) => <div><div className="text-[10px] uppercase text-slate-500">{l}</div><div className={`text-base font-bold tabular ${c}`}>{v}</div></div>;

function Totals({ a, testid }) {
  return (
    <div className="flex flex-wrap gap-x-5 gap-y-1" data-testid={testid}>
      {Object.entries(a.items || {}).map(([k, v]) => <Stat key={k} l={k} v={v} />)}<Stat l="Incassato" v={eur(a.incassato)} />
      <Stat l="A te" v={eur(a.nostro)} c="text-emerald-700" /><Stat l="A terzi (fotografo)" v={eur(a.fotografo)} /><Stat l="Costi articoli" v={eur(a.costo_magliette)} />
    </div>
  );
}

export default function MarketingSection() {
  const [month, setMonth] = useState(today().slice(0, 7));
  const [cfg, setCfg] = useState(null);
  const [sum, setSum] = useState(null);
  const [rows, setRows] = useState([]);
  const [view, setView] = useState("weeks");
  const [showItems, setShowItems] = useState(false);
  const loadCfg = () => { api.get("/marketing/settings").then((r) => setCfg(r.data)); };
  const load = useCallback(() => {
    api.get(`/marketing/summary?month=${month}`).then((r) => setSum(r.data));
    api.get(`/marketing/sales?month=${month}`).then((r) => setRows(r.data));
  }, [month]);
  useEffect(() => { loadCfg(); }, []);
  useEffect(() => { load(); }, [load]);
  const del = async (id) => { if (window.confirm("Eliminare questa vendita?")) { await api.delete(`/marketing/sales/${id}`); load(); } };
  const resend = async (r) => {
    const phone = r.client_phone || window.prompt("Numero WhatsApp del cliente", "") || "";
    try { await sendMarketingReceipt({ ...r, client_phone: phone }); load(); } catch (e) { toast.error(formatApiError(e)); }
  };
  if (!cfg || !sum) return null;
  return (
    <Card className="p-5 mb-6 space-y-4" data-testid="marketing-section">
      <div className="flex flex-wrap items-center gap-3">
        <h3 className="font-heading font-bold text-slate-900">Marketing</h3>
        <span className="text-xs text-emerald-700 font-semibold">entrata (+), non spesa</span>
        <button data-testid="mk-items-toggle" onClick={() => setShowItems(!showItems)} className="text-xs font-semibold text-sky-700 inline-flex items-center gap-1"><Settings2 size={13} /> Articoli</button>
        <input data-testid="mk-month" type="month" value={month} onChange={(e) => setMonth(e.target.value)} className={`${inp} ml-auto`} />
      </div>
      {showItems && <MarketingItems cfg={cfg} onSaved={loadCfg} />}
      <SaleForm key={cfg.items.map((x) => x.id + x.price + x.our_pct).join()} cfg={cfg} onSaved={load} />
      <div className="rounded-lg bg-slate-50 p-3"><div className="text-xs font-semibold text-slate-600 mb-1">Totale mese</div><Totals a={sum.month} testid="mk-month-totals" />
        {Object.keys(sum.by_method).length > 0 && <div className="text-[11px] text-slate-500 mt-1">Per pagamento: {Object.entries(sum.by_method).map(([k, v]) => `${k} ${eur(v)}`).join(" · ")}</div>}
      </div>
      <div className="flex gap-1">
        {[["weeks", "Per settimana"], ["days", "Per giorno"], ["list", "Vendite"]].map(([k, l]) => (
          <button key={k} data-testid={`mk-view-${k}`} onClick={() => setView(k)} className={`px-3 py-1 rounded text-xs font-semibold ${view === k ? "bg-sky-700 text-white" : "bg-white border border-slate-200 text-slate-600"}`}>{l}</button>
        ))}
      </div>
      {view !== "list" && (sum[view].length ? sum[view].map((r) => (
        <div key={r.week_start || r.date} className="border-b border-slate-100 pb-2" data-testid={`mk-${view}-${r.week_start || r.date}`}>
          <div className="text-xs font-semibold text-slate-700 mb-1">{view === "weeks" ? `Settimana dal ${r.week_start}` : r.date}</div><Totals a={r} />
        </div>
      )) : <div className="text-sm text-slate-400">Nessuna vendita nel mese</div>)}
      {view === "list" && (
        <div className="overflow-x-auto"><table className="w-full text-xs min-w-[640px]" data-testid="mk-list">
          <tbody>{rows.map((r) => (
            <tr key={r.id} className="border-b border-slate-100" data-testid={`mk-row-${r.id}`}>
              <td className="py-1.5">{r.date}<div className="text-[10px] text-slate-400">{r.receipt_no}</div></td><td className="font-semibold">{r.item_name || r.type} ×{r.qty}</td><td>{r.client}{r.notes && <div className="text-[10px] text-slate-400" data-testid={`mk-sale-notes-${r.id}`}>{r.notes}</div>}</td><td>{r.payment_method}</td>
              <td className="text-right tabular">{eur(r.total)}</td><td className="text-right tabular text-emerald-700">{eur(r.our_amount)}</td>
              <td className="text-right text-slate-400">{r.mode === "percent" || r.type === "foto" ? `a terzi ${eur(r.photographer_amount)}` : `costo ${eur(r.cost)}`}</td>
              <td className="text-right whitespace-nowrap"><button data-testid={`mk-receipt-${r.id}`} title="Invia ricevuta su WhatsApp" onClick={() => resend(r)} className={`mr-2 ${r.receipt_sent_at ? "text-emerald-600" : "text-green-700"}`}><Send size={13} /></button><button data-testid={`mk-del-${r.id}`} onClick={() => del(r.id)} className="text-rose-500"><Trash2 size={13} /></button></td>
            </tr>
          ))}</tbody>
        </table></div>
      )}
      <p className="text-[11px] text-slate-400">In Annuale e nei report tutto l'incassato va nelle entrate; quota di terzi (es. fotografo) e costi degli articoli vanno nelle spese alla voce "Marketing".</p>
    </Card>
  );
}
